import { useLiveQuery } from 'dexie-react-hooks';
import type { Customer } from '@sense/shared';
import { db } from './db';
import { afterQueued } from './sync';
import { sharedQuery } from './shared-query';

export type CustomerRef = Pick<Customer, 'id' | 'name' | 'phone'>;

/** Create or edit a customer on this device; it reaches the server with the next sync (works offline). */
export async function saveCustomer(input: { id?: string; name: string; phone: string; email?: string | null; notes?: string | null; credit_limit?: number | null }): Promise<Customer> {
  const existing = input.id ? await db.customers.get(input.id) : await db.customers.where('phone').equals(input.phone).first();
  const c: Customer = {
    id: existing?.id ?? crypto.randomUUID(),
    name: input.name.trim(),
    phone: input.phone,
    email: input.email ?? existing?.email ?? null,
    notes: input.notes ?? existing?.notes ?? null,
    credit_limit: input.credit_limit !== undefined ? input.credit_limit : existing?.credit_limit ?? null,
    balance: existing?.balance ?? 0,
    points: existing?.points ?? 0,
    total_spent: existing?.total_spent ?? 0,
    visits: existing?.visits ?? 0,
    last_visit: existing?.last_visit ?? null,
  };
  await db.transaction('rw', db.customers, db.outbox, async () => {
    await db.customers.put(c);
    await db.outbox.add({ kind: 'customer', doc: { id: c.id, name: c.name, phone: c.phone, email: c.email, notes: c.notes, credit_limit: c.credit_limit } });
  });
  void afterQueued();
  return c;
}

/** Name / phone search for the billing picker (all on the device). */
export function searchCustomers(list: Customer[], q: string, limit = 8): Customer[] {
  const t = q.trim().toLowerCase();
  if (!t) return list.slice().sort((a, b) => (b.last_visit ?? 0) - (a.last_visit ?? 0)).slice(0, limit);
  const digits = t.replace(/\D/g, '');
  return list
    .filter((c) => (digits.length >= 3 && c.phone.includes(digits)) || c.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(t)) || c.name.toLowerCase().includes(t))
    .slice(0, limit);
}

const customersQ = sharedQuery(() => db.customers.toArray());

/** All customers, shared live by every screen. */
export function useCustomers(): Customer[] | undefined {
  return customersQ.use();
}

export function useCustomer(id: string | null | undefined): Customer | undefined {
  return useLiveQuery(() => (id ? db.customers.get(id) : undefined), [id]);
}

/** WhatsApp link with a polite khata reminder (opens WhatsApp on phones, web.whatsapp on desktop). */
export function reminderLink(c: Customer, store: string, amount: string) {
  const text = `Hello ${c.name}, this is a friendly reminder from ${store}: your pending balance is ${amount}. Thank you!`;
  return `https://wa.me/91${c.phone}?text=${encodeURIComponent(text)}`;
}
